import React, { useRef, useState } from 'react';
import {
	SpaceBetween,
	FormField,
	Input,
	Textarea,
	Select,
	SelectProps,
	Toggle,
	Calendar,
	Grid,
	Button,
	Box,
	Popover,
	PopoverProps,
	StatusIndicator,
} from '@amzn/awsui-components-console';
import { FlashbarType } from '../FeatureType';
import { ClockTimePicker } from './ClockTimePicker';
import { FlashbarTemplate } from './FlashbarFormTemplates';

const typeOptions: SelectProps.Option[] = [
	{ label: 'Info', value: FlashbarType.INFO },
	{ label: 'Success', value: FlashbarType.SUCCESS },
	{ label: 'Warning', value: FlashbarType.WARNING },
	{ label: 'Error', value: FlashbarType.ERROR },
];

const DEFAULT_TIME = '00:00';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

interface ExpiryPreset {
	label: string;
	offsetMs: number;
	testId: string;
}

const expiryPresets: ExpiryPreset[] = [
	{ label: '1 hour', offsetMs: HOUR_MS, testId: '1-hour' },
	{ label: '6 hours', offsetMs: 6 * HOUR_MS, testId: '6-hours' },
	{ label: '1 day', offsetMs: DAY_MS, testId: '1-day' },
	{ label: '3 days', offsetMs: 3 * DAY_MS, testId: '3-days' },
	{ label: '1 week', offsetMs: 7 * DAY_MS, testId: '1-week' },
	{ label: '30 days', offsetMs: 30 * DAY_MS, testId: '30-days' },
];

const localTimeZone =
	Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';

const pad = (num: number) => String(num).padStart(2, '0');

const formatTimeLabel = (time24: string): string => {
	const [hours, minutes] = time24.split(':').map(Number);
	const period = hours < 12 ? 'AM' : 'PM';
	const hours12 = hours % 12 === 0 ? 12 : hours % 12;

	return `${hours12}:${pad(minutes)} ${period}`;
};

const EXPIRY_PREVIEW_FORMAT: Intl.DateTimeFormatOptions = {
	weekday: 'long',
	year: 'numeric',
	month: 'long',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
};

const getTimezoneOffset = (date: Date): string => {
	const offset = -date.getTimezoneOffset();
	const sign = offset >= 0 ? '+' : '-';
	const abs = Math.abs(offset);
	return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
};

const toLocalExpiryValue = (date: Date): string =>
	`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
	`T${pad(date.getHours())}:${pad(date.getMinutes())}${getTimezoneOffset(date)}`;

const formatDateLabel = (isoDate: string): string => {
	const [year, month, day] = isoDate.split('-');

	return `${day}/${month}/${year}`;
};

const startOfToday = (): number => {
	const date = new Date();

	date.setHours(0, 0, 0, 0);

	return date.getTime();
};

const calendarI18nStrings = {
	todayAriaLabel: 'Today',
	nextMonthAriaLabel: 'Next month',
	previousMonthAriaLabel: 'Previous month',
	nextYearAriaLabel: 'Next year',
	previousYearAriaLabel: 'Previous year',
};

const formatRelativeToNow = (target: Date): string => {
	const diffMs = target.getTime() - Date.now();
	const isPast = diffMs < 0;
	const absMinutes = Math.round(Math.abs(diffMs) / 60000);

	let value: number;
	let unit: string;

	if (absMinutes < 60) {
		value = Math.max(absMinutes, 1);
		unit = 'minute';
	} else if (absMinutes < 60 * 24) {
		value = Math.round(absMinutes / 60);
		unit = 'hour';
	} else {
		value = Math.round(absMinutes / (60 * 24));
		unit = 'day';
	}

	const plural = value === 1 ? '' : 's';

	return isPast
		? `${value} ${unit}${plural} ago`
		: `in ${value} ${unit}${plural}`;
};

interface FlashbarFormProps {
	type: SelectProps.Option;
	header: string;
	content: string;
	isGlobal: boolean;
	isBlockingExecution: boolean;
	expiresAt: string;
	expiresAtError?: string;
	onTypeChange: (option: SelectProps.Option) => void;
	onHeaderChange: (value: string) => void;
	onContentChange: (value: string) => void;
	onGlobalChange: (value: boolean) => void;
	onBlockingExecutionChange: (value: boolean) => void;
	onExpiresAtChange: (value: string) => void;
	templates?: FlashbarTemplate[];
	onTemplateSelect?: (template: FlashbarTemplate) => void;
	onSaveAsTemplate?: (template: Omit<FlashbarTemplate, 'id'>) => Promise<void> | void;
}

export const FlashbarForm: React.FC<FlashbarFormProps> = ({
	type,
	header,
	content,
	isGlobal,
	isBlockingExecution,
	expiresAt,
	expiresAtError,
	onTypeChange,
	onHeaderChange,
	onContentChange,
	onGlobalChange,
	onBlockingExecutionChange,
	onExpiresAtChange,
	templates,
	onTemplateSelect,
	onSaveAsTemplate,
}) => {
	const hasExpiry = expiresAt?.includes('T');
	const expiryDate = hasExpiry ? expiresAt.split('T')[0] : '';
	const rawTime = hasExpiry ? expiresAt.split('T')[1] : '';
	const expiryTime = rawTime ? rawTime.substring(0, 5) : '';

	const now = new Date();
	const todayStr = toLocalExpiryValue(now).split('T')[0];
	const nowMinutes = now.getHours() * 60 + now.getMinutes();

	const firstSelectableTimeFor = (dateStr: string): string => {
		if (dateStr !== todayStr) {
			return DEFAULT_TIME;
		}

		const nextSlot = Math.ceil((nowMinutes + 1) / 30) * 30;

		if (nextSlot >= 24 * 60) {
			return DEFAULT_TIME;
		}

		return `${pad(Math.floor(nextSlot / 60))}:${pad(nextSlot % 60)}`;
	};

	const datePopoverRef = useRef<PopoverProps.Ref>(null);
	const timePopoverRef = useRef<PopoverProps.Ref>(null);

	const [selectedPresetMs, setSelectedPresetMs] = useState<number | null>(null);
	const handleDateChange = (date: string) => {
		setSelectedPresetMs(null);

		if (!date) {
			onExpiresAtChange('');

			return;
		}

		const time = expiryTime || firstSelectableTimeFor(date);

		onExpiresAtChange(`${date}T${time}${getTimezoneOffset(new Date())}`);
	};

	const handleTimeChange = (time: string) => {
		setSelectedPresetMs(null);
		const date = expiryDate || todayStr;

		onExpiresAtChange(`${date}T${time}${getTimezoneOffset(new Date())}`);
	};

	const handleCalendarChange = (date: string) => {
		handleDateChange(date);
		datePopoverRef.current?.dismiss();
	};

	const clearExpiry = () => {
		setSelectedPresetMs(null);
		onExpiresAtChange('');
	};


	const applyPreset = (offsetMs: number) => {
		setSelectedPresetMs(offsetMs);
		onExpiresAtChange(toLocalExpiryValue(new Date(Date.now() + offsetMs)));
	};

	const isDateEnabled = (date: Date) => date.getTime() >= startOfToday();

	const parsedExpiry = hasExpiry && expiryTime ? new Date(expiresAt) : null;
	const hasValidExpiry =
		parsedExpiry !== null && !Number.isNaN(parsedExpiry.getTime());

	const expiryPreview = hasValidExpiry
		? parsedExpiry!.toLocaleString(undefined, EXPIRY_PREVIEW_FORMAT)
		: '';

	const [selectedTemplate, setSelectedTemplate] = useState<SelectProps.Option | null>(null);

	const templateOptions: SelectProps.Options = [
		...(templates || []).map((t) => ({
			label: t.label,
			value: t.id,
			description: t.description,
		})),
		...(onSaveAsTemplate ? [{ label: '➕ Save current as template', value: '__save_new__' }] : []),
	];

	const [templateSaveError, setTemplateSaveError] = useState<string | null>(null);

	const handleTemplateChange = async (option: SelectProps.Option) => {
		if (option.value === '__save_new__' && onSaveAsTemplate) {
			try {
				setTemplateSaveError(null);
				await onSaveAsTemplate({
					label: header || 'Untitled Template',
					description: content.substring(0, 60) || 'Custom template',
					type: type.value as FlashbarType,
					header,
					content,
					isGlobal,
					isBlockingExecution,
				});
			} catch {
				setTemplateSaveError('Failed to save template. Please try again.');
			}
			return;
		}

		const selected = (templates || []).find((t) => t.id === option.value);

		if (selected && onTemplateSelect) {
			setSelectedTemplate(option);
			onTemplateSelect(selected);
		}
	};

	return (
		<SpaceBetween size="l">
			{templateOptions.length > 0 && (
				<FormField label="Use Template" description="Pre-fill form with a common scenario" errorText={templateSaveError || undefined}>
					<Select
						data-testid="flashbar-template-select"
						selectedOption={selectedTemplate}
						onChange={({ detail }) => handleTemplateChange(detail.selectedOption)}
						options={templateOptions}
						placeholder="Select a template..."
					/>
				</FormField>
			)}
			<FormField label="Type">
				<Select
					data-testid="flashbar-type-select"
					selectedOption={type}
					onChange={({ detail }) => onTypeChange(detail.selectedOption)}
					options={typeOptions}
				/>
			</FormField>

			<FormField label="Header">
				<Input
					data-testid="flashbar-header-input"
					value={header}
					placeholder="Enter flashbar header"
					onChange={({ detail }) => onHeaderChange(detail.value)}
				/>
			</FormField>

			<FormField label="Content">
				<Textarea
					data-testid="flashbar-content-textarea"
					value={content}
					rows={3}
					placeholder="Enter flashbar content message"
					onChange={({ detail }) => onContentChange(detail.value)}
				/>
			</FormField>

			<SpaceBetween size="xs">
				<SpaceBetween direction="horizontal" size="xs" alignItems="center">
					<Box variant="awsui-key-label">Global Alert</Box>
					<Toggle
						data-testid="flashbar-global-toggle"
						checked={isGlobal}
						onChange={({ detail }) => onGlobalChange(detail.checked)}
					>
						{isGlobal
							? 'Visible on all pages'
							: "Visible only on What's New page"}
					</Toggle>
				</SpaceBetween>
				<Box variant="small" color="text-body-secondary">
					When enabled, this flashbar will be displayed across all pages of the
					application
				</Box>
			</SpaceBetween>

			<SpaceBetween size="xs">
				<SpaceBetween direction="horizontal" size="xs" alignItems="center">
					<Box variant="awsui-key-label">Block Executions</Box>
					<Toggle
						data-testid="flashbar-block-execution-toggle"
						checked={isBlockingExecution}
						disabled={!isGlobal}
						onChange={({ detail }) => onBlockingExecutionChange(detail.checked)}
					>
						{isBlockingExecution
							? 'Executions will be blocked'
							: 'Executions will not be blocked'}
					</Toggle>
				</SpaceBetween>
				<Box variant="small" color="text-body-secondary">
					When enabled, this alert will prevent all workflow executions (except
					for users with override permission)
				</Box>
			</SpaceBetween>

			<FormField
				label={
					<>
						Expiry date and time{' '}
						<Box
							variant="small"
							display="inline"
							color="text-body-secondary"
							fontWeight="normal"
						>
							(optional)
						</Box>
					</>
				}
				description={`Choose when this flashbar should automatically expire. Times are in your local time zone (${localTimeZone}). Leave empty for no expiry.`}
				errorText={expiresAtError}
			>
				<SpaceBetween size="s">
					<Box variant="awsui-key-label">Quick select</Box>
					<SpaceBetween direction="horizontal" size="xs">
						{expiryPresets.map((preset) => (
							<Button
								key={preset.label}
								variant={selectedPresetMs === preset.offsetMs ? 'primary' : 'normal'}
								data-testid={`flashbar-expiry-preset-${preset.testId}`}
								onClick={() => applyPreset(preset.offsetMs)}
							>
								{preset.label}
							</Button>
						))}
						{hasExpiry && (
							<Button
								variant="normal"
								iconName="close"
								data-testid="flashbar-expiry-clear"
								onClick={clearExpiry}
							>
								No expiry
							</Button>
						)}
					</SpaceBetween>

					<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
						<FormField label="Date" stretch>
							<Popover
								ref={datePopoverRef}
								triggerType="custom"
								position="top"
								size="large"
								dismissButton={false}
								content={
									<Calendar
										data-testid="flashbar-expiry-date"
										value={expiryDate}
										onChange={({ detail }) =>
											handleCalendarChange(detail.value)
										}
										locale="en-GB"
										isDateEnabled={isDateEnabled}
										dateDisabledReason={() =>
											'Expiry date cannot be in the past'
										}
										i18nStrings={calendarI18nStrings}
										ariaLabel="Expiry date"
									/>
								}
							>
								<Button
									data-testid="flashbar-expiry-date-trigger"
									iconName="calendar"
								>
									{expiryDate ? formatDateLabel(expiryDate) : 'Select a date'}
								</Button>
							</Popover>
						</FormField>
						<FormField label="Time" stretch>
							<Popover
								ref={timePopoverRef}
								triggerType="custom"
								position="top"
								size="large"
								dismissButton={false}
								content={
									<SpaceBetween size="s">
										<ClockTimePicker
											value={expiryTime}
											onChange={handleTimeChange}
										/>
										<Box float="right">
											<Button
												variant="primary"
												data-testid="flashbar-expiry-time-done"
												onClick={() => timePopoverRef.current?.dismiss()}
											>
												Done
											</Button>
										</Box>
									</SpaceBetween>
								}
							>
								<Button
									data-testid="flashbar-expiry-time-trigger"
									iconName="status-pending"
									iconAlign="right"
									fullWidth
								>
									{expiryTime ? formatTimeLabel(expiryTime) : 'Select a time'}
								</Button>
							</Popover>
						</FormField>
					</Grid>

					{hasValidExpiry && !expiresAtError && (
						<StatusIndicator type="info">
							Expires {expiryPreview} ({formatRelativeToNow(parsedExpiry!)})
						</StatusIndicator>
					)}

					{hasExpiry && !expiryTime && (
						<Box variant="small" color="text-status-inactive">
							Enter a time to set the exact expiry moment.
						</Box>
					)}
				</SpaceBetween>
			</FormField>
		</SpaceBetween>
	);
};
